import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { getDLToken, ensureDLToken } from "@/lib/dlToken";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Download, RefreshCw, Mail, Send, RotateCcw, Check, CircleDashed, Eye, BellRing, Pencil, Trash2, Search, CreditCard } from "lucide-react";
import StatsScopeToggle from "@/components/StatsScopeToggle";
import { makeScopeFilter } from "@/lib/scope";
import WeeklyDraftsPanel from "@/components/WeeklyDraftsPanel";
import { AutopayDeclinesBanner } from "@/components/AutopayDeclinesBanner";

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function AdminInvoices() {
  const [pharmacies, setPharmacies] = useState([]);
  const [pharmacyId, setPharmacyId] = useState("");
  const [from, setFrom] = useState(isoMonthStart());
  const [to, setTo] = useState(isoToday());
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({}); // order_id -> price (number)
  const [invoiceDate, setInvoiceDate] = useState(isoToday()); // user-overridable; defaults to today
  const [invoiceNumber, setInvoiceNumber] = useState(""); // user-overridable; pre-filled with next auto-number
  const [nextAutoNumber, setNextAutoNumber] = useState(""); // what the server would auto-assign
  const [history, setHistory] = useState([]);
  // Week / Month / YTD pill — filters the Recent invoices list by
  // `sent_at`. Defaults to Week to match the rest of the platform.
  const [historyScope, setHistoryScope] = useState("week");
  // Free-text filters applied AFTER the Week/Month/YTD pill. Pharmacy
  // substring + invoice-number substring — both case-insensitive, both
  // tolerant of whitespace. (2026-10-07 request.)
  const [phFilter, setPhFilter] = useState("");
  const [invFilter, setInvFilter] = useState("");
  const scopedHistory = useMemo(() => {
    const fn = makeScopeFilter(historyScope);
    const ph = phFilter.trim().toLowerCase();
    const inv = invFilter.trim().toLowerCase();
    return history.filter((h) => {
      if (!fn({ created_at: h.sent_at })) return false;
      if (ph && !(h.pharmacy_name || "").toLowerCase().includes(ph)) return false;
      if (inv && !(h.invoice_number || "").toLowerCase().includes(inv)) return false;
      return true;
    });
  }, [history, historyScope, phFilter, invFilter]);
  const [outstanding, setOutstanding] = useState({ count: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: "", subject: "", body: "", sending: false });

  const loadHistory = () => {
    api.get("/admin/invoices/history", { params: { limit: 50 } })
      .then((r) => setHistory(r.data))
      .catch(() => {});
    api.get("/admin/invoices/outstanding-summary", { params: { scope: historyScope } })
      .then((r) => setOutstanding(r.data))
      .catch(() => {});
  };

  // Refetch the Outstanding tile whenever the scope pill changes so the
  // receivables headline tracks the window the dispatcher is reviewing.
  useEffect(() => {
    api.get("/admin/invoices/outstanding-summary", { params: { scope: historyScope } })
      .then((r) => setOutstanding(r.data))
      .catch(() => {});
  }, [historyScope]);

  useEffect(() => {
    api.get("/pharmacies").then((r) => {
      setPharmacies(r.data);
      if (r.data.length && !pharmacyId) setPharmacyId(r.data[0].id);
    });
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreview = async () => {
    if (!pharmacyId) { toast.error("Pick a pharmacy"); return; }
    setLoading(true);
    setPreview(null);
    setOverrides({});
    try {
      const [{ data }, nextNumResp] = await Promise.all([
        api.get("/admin/invoices/preview", { params: { pharmacy_id: pharmacyId, date_from: from, date_to: to } }),
        api.get("/admin/invoices/next-number").catch(() => ({ data: { invoice_number: "" } })),
      ]);
      setPreview(data);
      const nextNum = nextNumResp.data?.invoice_number || "";
      setNextAutoNumber(nextNum);
      setInvoiceNumber(nextNum); // prefill editable field with the auto-generated next number
      setInvoiceDate(isoToday()); // reset to today on each preview
      if (data.totals.count === 0) toast.info("No delivered orders in this period.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Recompute totals using the override map.
  const liveRows = useMemo(() => {
    if (!preview) return [];
    return preview.rows.map((r) => ({ ...r, price: overrides[r.order_id] != null ? Number(overrides[r.order_id]) : r.price }));
  }, [preview, overrides]);
  const liveTotalPrice = useMemo(() => liveRows.reduce((s, r) => s + (Number.isFinite(r.price) ? r.price : 0), 0), [liveRows]);
  const liveTotalMiles = useMemo(() => liveRows.reduce((s, r) => s + (Number.isFinite(r.miles) ? r.miles : 0), 0), [liveRows]);

  const downloadPdf = async () => {
    if (!preview) return;
    setGenerating(true);
    try {
      const trimmedNo = (invoiceNumber || "").trim();
      const usingOverride = trimmedNo && trimmedNo !== nextAutoNumber;
      const resp = await api.post(
        "/admin/invoices/pdf",
        {
          pharmacy_id: pharmacyId,
          date_from: from,
          date_to: to,
          price_overrides: overrides,
          invoice_date: invoiceDate,
          invoice_number_override: usingOverride ? trimmedNo : null,
        },
        { responseType: "blob", params: { persist: true } },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice-${preview.pharmacy.name.replace(/[^\w]+/g, "_")}-${from}_${to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Invoice generated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const openCompose = () => {
    if (!preview || preview.totals.count === 0) return;
    const co = preview.company || {};
    const ph = preview.pharmacy;
    // Invoice # shown in subject: prefer the explicit override, otherwise
    // fall back to whatever the server would auto-assign next.
    const trimmedNo = (invoiceNumber || "").trim();
    const invoiceNoForSubject = trimmedNo || nextAutoNumber || "";
    const body =
`Hello ${ph.name},

Please find attached your invoice for delivery services performed between ${preview.period.from} and ${preview.period.to}.

Total amount due: $${liveTotalPrice.toFixed(2)}
Orders billed: ${liveRows.length}

Payment is due on receipt. Reply to this email if anything looks off.

Thanks,
${co.name || ""}${co.phone ? `\n${co.phone}` : ""}${co.email ? ` · ${co.email}` : ""}${co.website ? `\n${co.website}` : ""}`;
    setCompose({
      to: ph.billing_email || "",
      subject: `Invoice — ${ph.name}${invoiceNoForSubject ? ` (${invoiceNoForSubject})` : ""}`,
      body,
      sending: false,
    });
    setComposeOpen(true);
  };

  const sendInvoice = async () => {
    if (!compose.to || !compose.to.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setCompose((c) => ({ ...c, sending: true }));
    try {
      const trimmedNo = (invoiceNumber || "").trim();
      const usingOverride = trimmedNo && trimmedNo !== nextAutoNumber;
      const { data } = await api.post("/admin/invoices/send", {
        pharmacy_id: pharmacyId,
        date_from: from,
        date_to: to,
        to_email: compose.to,
        subject: compose.subject,
        body_text: compose.body,
        price_overrides: overrides,
        invoice_date: invoiceDate,
        invoice_number_override: usingOverride ? trimmedNo : null,
      });
      toast.success(`Invoice ${data.invoice_number} emailed to ${data.to}`);
      setComposeOpen(false);
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCompose((c) => ({ ...c, sending: false }));
    }
  };

  const [dlAuth, setDlAuth] = useState(() => getDLToken());
  useEffect(() => {
    ensureDLToken().then((t) => setDlAuth(t || "")).catch(() => {});
  }, []);

  const previewPdfHref = useMemo(() => {
    if (!pharmacyId || !from || !to || !dlAuth) return null;
    // SEC-003: short-lived download token instead of the long access JWT.
    const auth = encodeURIComponent(dlAuth);
    return `${API}/admin/invoices/pdf?pharmacy_id=${pharmacyId}&date_from=${from}&date_to=${to}&persist=false&auth=${auth}`;
  }, [pharmacyId, from, to, dlAuth]);

  // Delete an audit-log row (typos / dry-run test invoices). Removes from
  // Recent Invoices, P&L revenue, and the reminder scheduler queue. The
  // original email already went out — this is a bookkeeping cleanup only.
  const [chargingId, setChargingId] = useState(null);
  const chargeSavedCard = async (row) => {
    const c = row.autopay_card || {};
    const amount = (Number(row.total || 0) * 1.03).toFixed(2);
    const ok = window.confirm(
      `Charge invoice ${row.invoice_number} (${row.pharmacy_name}) to the saved card?\n\n` +
      `Card: ${c.brand || "Card"} ending ${c.last4 || "••••"}\n` +
      `Amount: $${amount} ($${Number(row.total || 0).toFixed(2)} + 3% card fee)\n\n` +
      "The invoice will be marked Paid and its emailed payment link deactivated."
    );
    if (!ok) return;
    setChargingId(row.id);
    try {
      const { data } = await api.post(`/admin/invoices/${row.id}/charge-saved-card`);
      setHistory((rows) => rows.map((r) => (r.id === row.id ? { ...r, payment_status: "paid", autopay_card: null } : r)));
      setOutstanding((o) => ({ count: Math.max(0, o.count - 1), total: Math.max(0, o.total - Number(row.total || 0)) }));
      toast.success(`Charged $${Number(data.amount_usd).toFixed(2)} — ${row.invoice_number} marked Paid`);
    } catch (e) {
      toast.error(formatApiError(e), { duration: 10000 });
    } finally {
      setChargingId(null);
    }
  };

  const deleteInvoice = async (row) => {
    if (!window.confirm(
      `Delete invoice ${row.invoice_number} from the audit log?\n\n` +
      `This only removes the record on our side — the email that was already sent to ${row.to_email || "the pharmacy"} won't be recalled.\n\n` +
      `The invoice will also be removed from the P&L and reminder queue.`
    )) return;
    try {
      await api.delete(`/admin/invoices/history/${row.id}`);
      toast.success(`Deleted ${row.invoice_number}`);
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Re-open the preview pre-populated from a past invoice. The user can then
  // tweak prices / date and click Email or Download — the original invoice #
  // is kept so the sequence never advances (manual override path on the backend).
  const reissue = async (row) => {
    setPharmacyId(row.pharmacy_id);
    setFrom((row.period_from || "").slice(0, 10));
    setTo((row.period_to || "").slice(0, 10));
    setLoading(true);
    setPreview(null);
    setOverrides({});
    try {
      const { data } = await api.get("/admin/invoices/preview", {
        params: { pharmacy_id: row.pharmacy_id, date_from: row.period_from, date_to: row.period_to },
      });
      setPreview(data);
      setInvoiceNumber(row.invoice_number || "");
      setNextAutoNumber(""); // ensures any value entered counts as an override
      setInvoiceDate((row.invoice_date || isoToday()).slice(0, 10));
      toast.success(`Loaded invoice ${row.invoice_number} for reissue`);
      // Scroll to top of preview
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Re-render the PDF for an already-sent invoice and open it in a new tab.
  // Uses persist=false + invoice_number_override so the sequence never bumps
  // — same approach as `reissue`, just without loading the editor. The modern
  // browser PDF viewer covers View / Download / Print in one place.
  const viewInvoicePdf = async (row) => {
    const t = toast.loading(`Opening ${row.invoice_number}…`);
    try {
      const { data } = await api.post(
        "/admin/invoices/pdf",
        {
          pharmacy_id: row.pharmacy_id,
          date_from: (row.period_from || "").slice(0, 10),
          date_to: (row.period_to || "").slice(0, 10),
          invoice_date: (row.invoice_date || "").slice(0, 10) || null,
          invoice_number_override: row.invoice_number,
        },
        { params: { persist: false }, responseType: "blob" }
      );
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Open in a new tab — browsers' built-in PDF viewer gives the user
      // print + download controls for free. Revoke the object URL after a
      // delay so the new tab has time to fetch the blob.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Pop-up blocked → fall back to forced download.
        const a = document.createElement("a");
        a.href = url;
        a.download = `Invoice-${row.invoice_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(`Opened ${row.invoice_number}`, { id: t });
    } catch (e) {
      toast.error(formatApiError(e), { id: t });
    }
  };

  // Toggle the payment status pill on a Recent Invoice row. Optimistically
  // updates the local list so the click feels instant, then reverts on error.
  const togglePaymentStatus = async (row) => {
    const next = row.payment_status === "paid" ? "pending" : "paid";
    const delta = next === "paid" ? -1 : 1; // we're MOVING a row out/into pending
    const amt = Number(row.total || 0);
    setHistory((rows) => rows.map((r) => r.id === row.id ? { ...r, payment_status: next } : r));
    // Optimistically update the tile so it reflects the change immediately.
    setOutstanding((o) => ({ count: o.count + delta, total: Math.max(0, o.total + delta * amt) }));
    try {
      await api.patch(`/admin/invoices/${row.id}/payment-status`, { payment_status: next });
      toast.success(`Invoice ${row.invoice_number} marked ${next}`);
    } catch (e) {
      // Revert on failure
      setHistory((rows) => rows.map((r) => r.id === row.id ? { ...r, payment_status: row.payment_status } : r));
      setOutstanding((o) => ({ count: o.count - delta, total: Math.max(0, o.total - delta * amt) }));
      toast.error(formatApiError(e));
    }
  };

  // Manual override for the daily reminder sweep. The scheduler already fires
  // at 9 AM ET every day, but Super Admin can click this to run the sweep
  // NOW (or preview via dry-run). Refreshes the history so the amber "Reminder
  // sent" badge appears on the affected rows.
  const [reminderBusy, setReminderBusy] = useState(false);
  const runReminders = async () => {
    setReminderBusy(true);
    try {
      const dry = await api.post("/admin/invoices/run-reminders", { dry_run: true });
      const eligible = dry.data?.eligible || 0;
      if (eligible === 0) {
        toast.info("No pending invoices past the 4-business-day threshold right now.");
        return;
      }
      const ok = window.confirm(
        `Send friendly reminder to ${eligible} pharmacy pharmac${eligible === 1 ? "y" : "ies"} whose invoices are past 3 business days pending?\n\n` +
        (dry.data.sent || []).slice(0, 8).map((s) => `• ${s.invoice_number} — ${s.pharmacy} (${s.bdays_pending} bday${s.bdays_pending === 1 ? "" : "s"})`).join("\n") +
        (eligible > 8 ? `\n…and ${eligible - 8} more` : "")
      );
      if (!ok) return;
      const res = await api.post("/admin/invoices/run-reminders", { dry_run: false });
      const sent = (res.data?.sent || []).length;
      const errs = (res.data?.errors || []).length;
      if (errs) toast.error(`${sent} reminder${sent === 1 ? "" : "s"} sent, ${errs} error${errs === 1 ? "" : "s"} — check server logs.`);
      else toast.success(`${sent} reminder${sent === 1 ? "" : "s"} sent successfully.`);
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReminderBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="admin-invoices-page">
      <header className="mb-6 flex items-start gap-3">
        <div className="h-10 w-10 rounded-full grid place-items-center bg-primary/10 text-primary"><FileText className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-display font-bold tracking-tight">Pharmacy Invoices</h1>
          <p className="text-muted-foreground text-sm mt-1">Generate, edit per-line prices, then email the invoice PDF straight to the pharmacy&apos;s billing contact.</p>
        </div>
        {/* Receivables tile — always shows aggregate of all pending invoices regardless of history page size. */}
        <div
          className={`shrink-0 rounded-xl px-4 py-3 border ${
            outstanding.count > 0
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : "border-emerald-300 bg-emerald-50 text-emerald-900"
          }`}
          data-testid="invoices-outstanding-tile"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-80">
            {outstanding.count > 0 ? `Outstanding · ${historyScope}` : "All settled"}
          </div>
          <div className="text-2xl font-mono-num font-bold leading-tight mt-0.5" data-testid="invoices-outstanding-total">
            ${Number(outstanding.total || 0).toFixed(2)}
          </div>
          <div className="text-[11px] opacity-80 mt-0.5">
            {outstanding.count} {outstanding.count === 1 ? "invoice" : "invoices"} pending
          </div>
        </div>
      </header>

      {/* Weekly Drafts — auto-prepped every Monday. Super Admin releases with the paper-plane icon. */}
      <AutopayDeclinesBanner refreshKey={history} onChanged={loadHistory} />
      <WeeklyDraftsPanel onDraftReleased={loadHistory} />

      {/* Filter row */}
      <Card className="p-5 rounded-xl border-border mb-5">
        <div className="grid md:grid-cols-[1fr,160px,160px,auto] gap-3 items-end">
          <div>
            <Label className="text-xs">Pharmacy</Label>
            <Select value={pharmacyId} onValueChange={setPharmacyId}>
              <SelectTrigger data-testid="invoice-pharmacy-select"><SelectValue placeholder="Pick a pharmacy" /></SelectTrigger>
              <SelectContent>{pharmacies.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="inv-from" className="text-xs">From date</Label>
            <Input id="inv-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="invoice-date-from" />
          </div>
          <div>
            <Label htmlFor="inv-to" className="text-xs">To date</Label>
            <Input id="inv-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="invoice-date-to" />
          </div>
          <Button type="button" variant="outline" onClick={runPreview} disabled={loading} className="rounded-full" data-testid="invoice-preview-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Loading…" : "Preview"}
          </Button>
        </div>
      </Card>

      {/* Preview */}
      {preview && (
        <Card className="rounded-xl border-border overflow-hidden" data-testid="invoice-preview">
          <div className="px-5 py-4 border-b border-border bg-secondary/30 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Bill to</div>
              <div className="font-medium">{preview.pharmacy.name}</div>
              <div className="text-xs text-muted-foreground">{preview.pharmacy.address}</div>
              {preview.pharmacy.billing_email && <div className="text-xs text-muted-foreground mt-1">✉ {preview.pharmacy.billing_email}</div>}
            </div>
            <div className="grid grid-cols-2 gap-3 min-w-[280px]">
              <div>
                <Label htmlFor="inv-no" className="text-xs">Invoice #</Label>
                <Input
                  id="inv-no"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder={nextAutoNumber || "SRCI-0014"}
                  className="font-mono-num h-9"
                  data-testid="invoice-number-input"
                />
                {invoiceNumber && invoiceNumber.trim() !== nextAutoNumber && (
                  <p className="text-[10px] text-amber-700 mt-0.5">Manual override — sequence won&apos;t advance</p>
                )}
              </div>
              <div>
                <Label htmlFor="inv-date" className="text-xs">Invoice date</Label>
                <Input
                  id="inv-date"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="h-9"
                  data-testid="invoice-date-input"
                />
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Period</div>
              <div className="font-mono-num text-sm">{preview.period.from} → {preview.period.to}</div>
            </div>
            <div className="flex gap-5">
              <Stat label="Orders" value={liveRows.length} />
              <Stat label="Miles" value={`${liveTotalMiles.toFixed(2)} mi`} />
              <Stat label="Total" value={`$${liveTotalPrice.toFixed(2)}`} accent />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type of Service</th>
                  <th className="px-3 py-2 text-left">Order #</th>
                  <th className="px-3 py-2 text-left">Patient Name</th>
                  <th className="px-3 py-2 text-left">Deliver Address</th>
                  <th className="px-3 py-2 text-right">Mileage</th>
                  <th className="px-3 py-2 text-right">Price <span className="ml-1 text-[10px] text-muted-foreground normal-case font-normal">(click to edit)</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {liveRows.map((r) => {
                  const overridden = overrides[r.order_id] != null;
                  return (
                    <tr key={r.order_id} className="hover:bg-secondary/30">
                      <td className="px-3 py-2 font-mono-num">{r.serviced}</td>
                      <td className="px-3 py-2">{r.service_type}</td>
                      <td className="px-3 py-2 font-mono-num">{r.order_number}</td>
                      <td className="px-3 py-2">{r.patient_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.address}</td>
                      <td className="px-3 py-2 font-mono-num text-right">{r.miles.toFixed(2)}</td>
                      <td className="px-3 py-2 font-mono-num text-right" data-testid={`invoice-row-price-${r.order_id}`}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={String(r.price)}
                          onChange={(e) => setOverrides((o) => ({ ...o, [r.order_id]: e.target.value === "" ? 0 : Number(e.target.value) }))}
                          className={`w-20 text-right px-1.5 py-1 rounded border bg-background ${overridden ? "border-primary/60 text-primary" : "border-border"}`}
                        />
                      </td>
                    </tr>
                  );
                })}
                {liveRows.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">No delivered orders in this period.</td></tr>
                )}
              </tbody>
              {liveRows.length > 0 && (
                <tfoot>
                  <tr className="bg-secondary/40 font-medium">
                    <td colSpan={5} className="px-3 py-2 text-right uppercase text-xs tracking-wider text-muted-foreground">Totals</td>
                    <td className="px-3 py-2 font-mono-num text-right">{liveTotalMiles.toFixed(2)}</td>
                    <td className="px-3 py-2 font-mono-num text-right text-primary">${liveTotalPrice.toFixed(2)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {liveRows.length > 0 && (
            <div className="px-5 py-4 border-t border-border bg-secondary/20 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-muted-foreground">
                Price edits stay client-side until you Generate or Email. Invoice number is stamped on Generate / Email.
                {previewPdfHref && (
                  <>{" · "}<a href={previewPdfHref} target="_blank" rel="noreferrer" className="text-primary hover:underline">Dry-run PDF</a></>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={downloadPdf} disabled={generating} className="rounded-full" data-testid="invoice-generate-btn">
                  <Download className="h-4 w-4 mr-1.5" /> {generating ? "Generating…" : "Download PDF"}
                </Button>
                <Button onClick={openCompose} variant="outline" className="rounded-full" data-testid="invoice-email-btn">
                  <Mail className="h-4 w-4 mr-1.5" /> Email to pharmacy
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Compose dialog */}
      <Dialog open={composeOpen} onOpenChange={(o) => !compose.sending && setComposeOpen(o)}>
        <DialogContent className="max-w-2xl" data-testid="invoice-compose-dialog">
          <DialogHeader>
            <DialogTitle>Email invoice to pharmacy</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="cmp-to" className="text-xs">To</Label>
              <Input id="cmp-to" type="email" value={compose.to} onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))} placeholder="billing@pharmacy.com" data-testid="invoice-compose-to" />
              {!preview?.pharmacy?.billing_email && <p className="text-[11px] text-amber-700 mt-1">This pharmacy has no billing email saved. Set one in Pharmacies admin so it pre-fills next time.</p>}
            </div>
            <div>
              <Label htmlFor="cmp-subject" className="text-xs">Subject</Label>
              <Input id="cmp-subject" value={compose.subject} onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))} data-testid="invoice-compose-subject" />
            </div>
            <div>
              <Label htmlFor="cmp-body" className="text-xs">Body</Label>
              <Textarea id="cmp-body" rows={10} value={compose.body} onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))} data-testid="invoice-compose-body" />
            </div>
            <div className="text-[11px] text-muted-foreground border-l-2 border-primary/30 pl-3">
              The invoice PDF will be attached automatically. Edits to per-line prices in the table above are included in the attached PDF.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)} disabled={compose.sending} className="rounded-full">Cancel</Button>
            <Button onClick={sendInvoice} disabled={compose.sending} className="rounded-full" data-testid="invoice-compose-send">
              <Send className="h-4 w-4 mr-1.5" /> {compose.sending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice history */}
      <Card className="rounded-xl border-border mt-6 overflow-hidden" data-testid="invoice-history-card">
        <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm font-medium">Recent invoices</div>
            <div className="text-[11px] text-muted-foreground">
              {scopedHistory.length} of last {history.length} shown · <span className="uppercase tracking-wider">{historyScope}</span>. Click &quot;Edit&quot; to reopen the preview with the same number, or &quot;Delete&quot; to remove from the audit log.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatsScopeToggle value={historyScope} onChange={setHistoryScope} data-testid="invoice-history-scope" />
            {/* Free-text filters sit alongside the scope pills so admins can
                drill into one pharmacy or one invoice without changing the
                date window. Both are case-insensitive substring matches. */}
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={phFilter}
                onChange={(e) => setPhFilter(e.target.value)}
                placeholder="Pharmacy…"
                className="h-8 pl-7 w-40 rounded-full text-xs"
                data-testid="invoice-history-filter-pharmacy"
              />
            </div>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={invFilter}
                onChange={(e) => setInvFilter(e.target.value)}
                placeholder="Invoice #…"
                className="h-8 pl-7 w-36 rounded-full text-xs font-mono-num"
                data-testid="invoice-history-filter-number"
              />
            </div>
            {(phFilter || invFilter) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-full px-2 text-muted-foreground"
                onClick={() => { setPhFilter(""); setInvFilter(""); }}
                data-testid="invoice-history-filter-clear"
                title="Clear filters"
              >
                Clear
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={runReminders}
              disabled={reminderBusy}
              className="rounded-full border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-500/20"
              title="Send friendly reminders now for invoices past 3 business days pending (the scheduler also runs this every day at 9 AM ET)."
              data-testid="invoice-run-reminders"
            >
              <BellRing className={`h-3.5 w-3.5 mr-1.5 ${reminderBusy ? "animate-pulse" : ""}`} />
              {reminderBusy ? "Checking…" : "Send reminders"}
            </Button>
            <Button variant="outline" size="sm" onClick={loadHistory} className="rounded-full" data-testid="invoice-history-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>
        {history.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">No invoices sent yet.</div>
        ) : scopedHistory.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid="invoice-history-empty-scope">
            No invoices match this filter. Try a wider window from the pills above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">Sent</th>
                  <th className="px-3 py-2 text-left">Invoice #</th>
                  <th className="px-3 py-2 text-left">Pharmacy</th>
                  <th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-left">To</th>
                  <th className="px-3 py-2 text-right">Rows</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-center">Status</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {scopedHistory.map((h) => (
                  <tr key={h.id} className="hover:bg-secondary/30" data-testid={`invoice-history-row-${h.invoice_number}`}>
                    <td className="px-3 py-2 font-mono-num text-xs text-muted-foreground">{(h.sent_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 font-mono-num font-medium">{h.invoice_number}</td>
                    <td className="px-3 py-2">{h.pharmacy_name}</td>
                    <td className="px-3 py-2 font-mono-num text-xs">{h.period_from} → {h.period_to}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{h.to_email}</td>
                    <td className="px-3 py-2 font-mono-num text-right">{h.rows}</td>
                    <td className="px-3 py-2 font-mono-num text-right">${Number(h.total || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => togglePaymentStatus(h)}
                        className={
                          `inline-flex items-center gap-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 transition-colors ${
                            h.payment_status === "paid"
                              ? "bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200"
                              : "bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-200"
                          }`
                        }
                        title={h.payment_status === "paid" ? "Click to mark Pending" : "Click to mark Paid"}
                        data-testid={`invoice-payment-status-${h.invoice_number}`}
                      >
                        {h.payment_status === "paid" ? (
                          <><Check className="h-3 w-3" /> Paid</>
                        ) : (
                          <><CircleDashed className="h-3 w-3" /> Pending</>
                        )}
                      </button>
                      {h.payment_status !== "paid" && h.reminder_sent_at && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300"
                          title={`Reminder emailed on ${(h.reminder_sent_at || "").slice(0, 16).replace("T", " ")}`}
                          data-testid={`invoice-reminder-sent-${h.invoice_number}`}
                        >
                          <BellRing className="h-3 w-3" /> Reminder sent
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-2 justify-end">
                        {h.payment_status !== "paid" && h.autopay_card && (
                          <Button
                            size="sm"
                            onClick={() => chargeSavedCard(h)}
                            disabled={chargingId === h.id}
                            className="rounded-full h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid={`invoice-charge-card-btn-${h.invoice_number}`}
                            title={`Charge ${h.autopay_card.brand || "card"} ending ${h.autopay_card.last4 || "••••"} (+3% fee)`}
                          >
                            <CreditCard className="h-3.5 w-3.5 mr-1" /> {chargingId === h.id ? "Charging…" : "Charge card"}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => viewInvoicePdf(h)}
                          className="rounded-full h-8 w-8 p-0"
                          data-testid={`invoice-view-pdf-btn-${h.invoice_number}`}
                          title="View / Download / Print PDF"
                          aria-label={`View invoice ${h.invoice_number} as PDF`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => reissue(h)}
                          className="rounded-full h-8"
                          data-testid={`invoice-edit-btn-${h.invoice_number}`}
                          title="Re-open this invoice in the editor (keeps the same #, sequence not bumped)"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteInvoice(h)}
                          className="rounded-full h-8 text-rose-700 border-rose-300 hover:bg-rose-50 hover:text-rose-800 dark:text-rose-300 dark:border-rose-800 dark:hover:bg-rose-900/20"
                          data-testid={`invoice-delete-btn-${h.invoice_number}`}
                          title="Remove this row from the audit log (P&L + reminder queue). Does not recall the email that was already sent."
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-lg border px-3 py-1.5 ${accent ? "border-primary/40 bg-primary/5" : "border-border"}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono-num text-base ${accent ? "text-primary font-semibold" : "font-medium"}`}>{value}</div>
    </div>
  );
}
